"use client";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarIcon, Edit, Trash } from "lucide-react";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CoachMarker } from "@/components/CoachMarker";
import { CreateTemplateDialog } from "@/features/calendar/components/templates/CreateTemplateDialog.tsx";
import { DeleteTemplateDialog } from "@/features/calendar/components/templates/DeleteTemplateDialog.tsx";
import { useTemplatesStore } from "@/features/calendar/store/useTemplatesStore";

const TemplatesList = () => {
  const { t } = useTranslation(["calendar", "common", "help"]);
  const [deleteTemplateId, setDeleteTemplateId] = useState<string | null>(null);
  const navigate = useNavigate();

  // Use the templates store
  const { templates, isLoadingTemplates, templatesError, fetchTemplates, resetErrors } =
    useTemplatesStore();

  useEffect(() => {
    // Reset errors when component mounts
    resetErrors();
    // Fetch templates
    fetchTemplates();
  }, []);

  // Find template by ID for dialogs
  const findTemplate = (id: string | null) => {
    if (!id) return null;

    return templates.find((template) => template.id === id);
  };

  // Get template for delete dialog
  const deleteTemplate = findTemplate(deleteTemplateId);

  // Render loading skeletons
  if (isLoadingTemplates) {
    return (
      <div className="bg-background min-h-[calc(100dvh_-_4rem)]">
        <ScrollArea className="h-[calc(100dvh_-_4rem)]">
          <div className="mx-auto max-w-6xl p-6">
            {/* Hero Header Skeleton */}
            <div className="bg-side-accent/50 relative mb-8 overflow-visible rounded-2xl border-none p-8">
              <div className="relative">
                <div className="mb-6 flex items-start justify-between">
                  <div className="flex items-center gap-6">
                    <Skeleton className="h-16 w-16 rounded-2xl" />
                    <div>
                      <Skeleton className="mb-2 h-8 w-48" />
                      <Skeleton className="h-5 w-64" />
                    </div>
                  </div>
                  <Skeleton className="h-12 w-32" />
                </div>
              </div>
            </div>
            {/* Template Cards Skeleton */}
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-20 w-full rounded-2xl" />
              ))}
            </div>
          </div>
        </ScrollArea>
      </div>
    );
  }

  // Render error state
  if (templatesError) {
    return (
      <div className="bg-background min-h-[calc(100dvh_-_4rem)]">
        <ScrollArea className="h-[calc(100dvh_-_4rem)]">
          <div className="mx-auto max-w-6xl p-6">
            <motion.div
              animate={{ opacity: 1, scale: 1 }}
              className="bg-foreground/50 flex h-96 flex-col items-center justify-center rounded-2xl backdrop-blur-xl"
              initial={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
            >
              <div className="bg-muted/20 mb-6 flex h-20 w-20 items-center justify-center rounded-2xl">
                <Icon
                  className="text-muted-foreground"
                  height={"2.5rem"}
                  icon="tabler:alert-triangle"
                  width={"2.5rem"}
                />
              </div>
              <h2 className="text-accent-foreground mb-2 text-xl font-semibold">
                {t("common:errors.genericErrorTitle")}
              </h2>
            </motion.div>
          </div>
        </ScrollArea>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-[calc(100dvh_-_4rem)]">
      <ScrollArea className="h-[calc(100dvh_-_4rem)]">
        <div className="mx-auto max-w-6xl p-2 min-[400px]:p-6">
          <title>{`${t("calendar:templates.title")} - Ximly`}</title>

          {/* Hero Header */}
          <CoachMarker
            description={t("help:coachMarkers.templatesOverview.description", {
              defaultValue:
                "Templates help you quickly create recurring schedules. You can set up weekly patterns or custom schedules that you can reuse anytime, saving time when planning your availability.",
            })}
            id="templates-overview"
            placement="top"
            title={t("help:coachMarkers.templatesOverview.title", {
              defaultValue: "Schedule Templates",
            })}
          >
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="bg-side-accent/50 text-accent-foreground relative mb-8 rounded-2xl border-none p-8 shadow-2xl max-[400px]:px-2"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4 }}
            >
              {/* Background Pattern - Wrapped in overflow hidden container */}
              <div className="absolute inset-0 overflow-hidden rounded-2xl">
                <div className="absolute -top-20 -left-20 h-40 w-40 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 opacity-5" />
                <div className="absolute -right-16 -bottom-16 h-32 w-32 rounded-full bg-gradient-to-tr from-cyan-400 to-blue-500 opacity-5" />
              </div>

              <div className="relative">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-6">
                    <motion.div
                      animate={{ scale: 1 }}
                      className="bg-muted/20 flex h-16 min-h-16 w-16 min-w-16 items-center justify-center rounded-2xl backdrop-blur-sm"
                      initial={{ scale: 0.8 }}
                      transition={{ duration: 0.3, delay: 0.2 }}
                    >
                      <Icon
                        className="text-accent-foreground"
                        height="2rem"
                        icon="tabler:template"
                        width="2rem"
                      />
                    </motion.div>
                    <div>
                      <h1 className="text-accent-foreground text-3xl leading-tight font-bold">
                        {t("calendar:templates.title")}
                      </h1>
                      <p className="text-muted-foreground mt-2 text-base">
                        {t("calendar:templates.description")}
                      </p>
                    </div>
                  </div>
                  <CreateTemplateDialog onSuccess={fetchTemplates}>
                    <Button
                      className="bg-accent-foreground/90 hover:bg-accent-foreground text-background my-auto rounded-xl px-6 py-3"
                      size="lg"
                    >
                      <Icon
                        className="=sm:mr-2"
                        height={"1.25rem"}
                        icon="tabler:plus"
                        width={"1.25rem"}
                      />
                      <span className={"max-sm:hidden"}>{t("calendar:templates.createNew")}</span>
                    </Button>
                  </CreateTemplateDialog>
                </div>
              </div>
            </motion.div>
          </CoachMarker>

          {templates.length === 0 ? (
            <CoachMarker
              description={t("help:coachMarkers.noTemplatesState.description", {
                defaultValue:
                  "You don't have any templates yet. Click the button below to create your first schedule template. Templates save you time by allowing you to reuse common schedule patterns.",
              })}
              id="no-templates-state"
              placement="top"
              title={t("help:coachMarkers.noTemplatesState.title", {
                defaultValue: "Create Your First Template",
              })}
            >
              <motion.div
                animate={{ opacity: 1, scale: 1 }}
                className="bg-foreground/50 relative rounded-2xl border-none p-12 text-center shadow-2xl backdrop-blur-xl"
                initial={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4 }}
              >
                {/* Background Pattern - Wrapped in overflow hidden container */}
                <div className="absolute inset-0 overflow-hidden rounded-2xl">
                  <div className="absolute -top-16 -right-16 h-32 w-32 rounded-full bg-gradient-to-br from-purple-400 to-pink-600 opacity-5" />
                  <div className="absolute -bottom-12 -left-12 h-24 w-24 rounded-full bg-gradient-to-tr from-orange-400 to-red-500 opacity-5" />
                </div>

                <div className="relative">
                  <div className="bg-muted/20 mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl backdrop-blur-sm">
                    <CalendarIcon className="text-muted-foreground h-10 w-10" />
                  </div>
                  <h2 className="text-accent-foreground mb-3 text-2xl font-semibold">
                    {t("calendar:templates.noTemplates")}
                  </h2>
                  <p className="text-muted-foreground mx-auto mb-8 max-w-md">
                    {t("calendar:templates.noTemplatesDescription")}
                  </p>
                  <CreateTemplateDialog onSuccess={fetchTemplates}>
                    <Button className="bg-accent-foreground/90 hover:bg-accent-foreground text-background rounded-xl px-6 py-3 text-lg">
                      <Icon className="mr-2 h-5 w-5" icon="tabler:plus" />
                      {t("calendar:templates.createFirst")}
                    </Button>
                  </CreateTemplateDialog>
                </div>
              </motion.div>
            </CoachMarker>
          ) : (
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="bg-foreground/50 relative rounded-2xl border-none shadow-2xl backdrop-blur-xl"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              {/* Background Pattern - Wrapped in overflow hidden container */}
              <div className="absolute inset-0 overflow-hidden rounded-2xl">
                <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br from-violet-400 to-purple-600 opacity-5" />
                <div className="absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-tr from-indigo-400 to-blue-500 opacity-5" />
              </div>

              <div className="relative p-0">
                <div className="flex max-w-[calc(100dvw_-_3rem)] flex-col max-[400px]:max-w-[calc(100dvw_-_1rem)]">
                  {templates.map((template, index) => (
                    <motion.div
                      key={template.id}
                      animate={{ opacity: 1, y: 0 }}
                      initial={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2, delay: index * 0.05 }}
                    >
                      {index === 0 ? (
                        <CoachMarker
                          description={t("help:coachMarkers.templateManagement.description", {
                            defaultValue:
                              "This is how each template appears in your list. Click anywhere on the template to edit its schedule. Use the edit button to rename it, or the delete button to remove it completely.",
                          })}
                          id="template-item-example"
                          placement="right"
                          title={t("help:coachMarkers.templateManagement.title", {
                            defaultValue: "Template Management",
                          })}
                        >
                          <div className="border-border/30 hover:bg-muted/20 group relative flex w-full max-w-full items-center justify-between overflow-hidden border-b p-6 transition-all duration-300 last:border-b-0">
                            <Link
                              className="flex min-w-0 flex-1 cursor-pointer items-center space-x-4 overflow-hidden"
                              to={`${template.id}/edit`}
                            >
                              <div
                                className={`flex size-12 min-h-12 min-w-12 flex-shrink-0 items-center justify-center rounded-2xl backdrop-blur-sm ${
                                  template.type === "weekly"
                                    ? "bg-emerald-500/20"
                                    : "bg-blue-500/20"
                                }`}
                              >
                                <Icon
                                  className={
                                    template.type === "weekly"
                                      ? "text-emerald-400"
                                      : "text-blue-400"
                                  }
                                  height="1.5rem"
                                  icon={
                                    template.type === "weekly"
                                      ? "tabler:calendar-week"
                                      : "tabler:calendar"
                                  }
                                  width="1.5rem"
                                />
                              </div>
                              <div className="min-w-0 flex-1 overflow-hidden">
                                <div className="flex min-w-0 items-start gap-3 overflow-hidden">
                                  <h3 className="text-accent-foreground min-w-0 truncate text-lg font-semibold">
                                    {template.name}
                                  </h3>
                                  <Badge
                                    className={`flex-shrink-0 rounded-full px-3 py-1 max-[450px]:hidden ${
                                      template.type === "weekly"
                                        ? "border-emerald-500/30 bg-emerald-500/20 text-emerald-400"
                                        : "border-blue-500/30 bg-blue-500/20 text-blue-400"
                                    }`}
                                  >
                                    {t(`calendar:templates.${template.type}`)}
                                  </Badge>
                                </div>
                                <div className="text-muted-foreground mt-2 flex items-center text-sm">
                                  <Icon
                                    className="mr-2"
                                    height="1rem"
                                    icon="tabler:clock"
                                    width="1rem"
                                  />
                                  {t("calendar:templates.slotsCount", {
                                    count: template.slots_count ?? 0,
                                  })}
                                </div>
                              </div>
                            </Link>
                            <div className="flex min-w-fit flex-shrink-0 items-center gap-2">
                              <TooltipProvider>
                                <Tooltip delayDuration={300}>
                                  <TooltipTrigger asChild>
                                    <Button
                                      className="h-10 w-10 p-0"
                                      size="icon"
                                      variant="ghost"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`${template.id}/edit`);
                                      }}
                                    >
                                      <Edit className="h-4 w-4" />
                                      <span className="sr-only">{t("common:edit")}</span>
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent align="center" side="top">
                                    <p>{t("common:edit")}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <TooltipProvider>
                                <Tooltip delayDuration={300}>
                                  <TooltipTrigger asChild>
                                    <Button
                                      className="h-10 w-10 bg-red-500/20 p-0 text-red-400 backdrop-blur-sm hover:bg-red-500/30"
                                      size="icon"
                                      variant="ghost"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDeleteTemplateId(template.id);
                                      }}
                                    >
                                      <Trash className="h-4 w-4" />
                                      <span className="sr-only">{t("common:delete")}</span>
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent align="center" side="top">
                                    <p>{t("common:delete")}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </div>
                        </CoachMarker>
                      ) : (
                        <div className="border-border/30 hover:bg-muted/20 group relative flex w-full max-w-full items-center justify-between overflow-hidden border-b p-6 transition-all duration-300 last:border-b-0">
                          <Link
                            className="flex min-w-0 flex-1 cursor-pointer items-center space-x-4 overflow-hidden"
                            to={`${template.id}/edit`}
                          >
                            <div
                              className={`flex size-12 min-h-12 min-w-12 flex-shrink-0 items-center justify-center rounded-2xl backdrop-blur-sm ${
                                template.type === "weekly" ? "bg-emerald-500/20" : "bg-blue-500/20"
                              }`}
                            >
                              <Icon
                                className={
                                  template.type === "weekly" ? "text-emerald-400" : "text-blue-400"
                                }
                                height="1.5rem"
                                icon={
                                  template.type === "weekly"
                                    ? "tabler:calendar-week"
                                    : "tabler:calendar"
                                }
                                width="1.5rem"
                              />
                            </div>
                            <div className="min-w-0 flex-1 overflow-hidden">
                              <div className="flex min-w-0 items-center gap-3 overflow-hidden">
                                <h3 className="text-accent-foreground min-w-0 truncate text-lg font-semibold">
                                  {template.name}
                                </h3>
                                <Badge
                                  className={`flex-shrink-0 rounded-full px-3 py-1 max-[450px]:hidden ${
                                    template.type === "weekly"
                                      ? "border-emerald-500/30 bg-emerald-500/20 text-emerald-400"
                                      : "border-blue-500/30 bg-blue-500/20 text-blue-400"
                                  }`}
                                >
                                  {t(`calendar:templates.${template.type}`)}
                                </Badge>
                              </div>
                              <div className="text-muted-foreground mt-2 flex items-center text-sm">
                                <Icon
                                  className="mr-2"
                                  height="1rem"
                                  icon="tabler:clock"
                                  width="1rem"
                                />
                                {t("calendar:templates.slotsCount", {
                                  count: template.slots_count ?? 0,
                                })}
                              </div>
                            </div>
                          </Link>
                          <div className="flex min-w-fit flex-shrink-0 items-center gap-2">
                            <TooltipProvider>
                              <Tooltip delayDuration={300}>
                                <TooltipTrigger asChild>
                                  <Button
                                    className="h-10 w-10 p-0"
                                    size="icon"
                                    variant="ghost"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`${template.id}/edit`);
                                    }}
                                  >
                                    <Edit className="h-4 w-4" />
                                    <span className="sr-only">{t("common:edit")}</span>
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent align="center" side="top">
                                  <p>{t("common:edit")}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip delayDuration={300}>
                                <TooltipTrigger asChild>
                                  <Button
                                    className="h-10 w-10 bg-red-500/20 p-0 text-red-400 backdrop-blur-sm hover:bg-red-500/30"
                                    size="icon"
                                    variant="ghost"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDeleteTemplateId(template.id);
                                    }}
                                  >
                                    <Trash className="h-4 w-4" />
                                    <span className="sr-only">{t("common:delete")}</span>
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent align="center" side="top">
                                  <p>{t("common:delete")}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Delete Template Dialog - Rendered outside of the card */}
          {deleteTemplate && (
            <DeleteTemplateDialog
              isOpen={!!deleteTemplateId}
              templateId={deleteTemplate.id}
              templateName={deleteTemplate.name}
              onClose={() => setDeleteTemplateId(null)}
              onSuccess={() => {
                fetchTemplates();
                setDeleteTemplateId(null);
              }}
            />
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default TemplatesList;
